import type { QueryClient } from "@tanstack/react-query";
import { USER_API } from "config";
import { ProductionState } from "config";
import { US_STATES } from "config/states";
import {
  CreateProjectDataType,
  ProjectDataType,
  ProjectTypeKey,
  SceneType,
} from "store/types/project";
import type { FormData } from "components/common/CreateProjectSheet/CreateProjectSheet.types";
import { getProfileFromCache } from "hooks/useProfile/useProfileCache";
import type { ProjectCreateSource } from "components/common/CreateProjectFlow/types/project-create-source.types";
import type { AxiosInstance } from "axios";
import { isAxiosError } from "axios";
import { uploadScreenplayViaSignedUrl } from "services/signedUpload";
import { finalDraftTagCountsFromImport, type FinalDraftTagCounts } from "services/finalDraftTags";

export const PROJECT_CREATE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

export type ParsedSceneLoose =
  | (Partial<SceneType> & { _location?: string | null })
  | null
  | undefined;
export interface ParsedContent {
  scenes?: ParsedSceneLoose[];
  [key: string]: unknown;
}

export type CreateProjectStage = "uploading" | "parsing" | "creating" | "finalizing";

/**
 * "Import" project creation goes through the async backend import-job pipeline
 * (direct-to-GCS upload + server-side parse/create) and survives page reloads.
 * On by default; REACT_APP_ASYNC_PROJECT_IMPORT="false" is the kill switch.
 * When the backend does not expose the endpoints yet (deploy skew), the flow
 * falls back to the legacy synchronous path automatically.
 */
export const isAsyncImportEnabled = (): boolean =>
  process.env.REACT_APP_ASYNC_PROJECT_IMPORT !== "false";

export interface CreateProjectExecuteInput {
  formData: FormData;
  file: File | null;
  parsedData: ParsedContent | null;
  mode: string;
  existingProject?: unknown;
  source?: ProjectCreateSource;
  workspaceId?: string;
}

export interface CreateProjectExecuteDeps {
  axios: AxiosInstance;
  queryClient: QueryClient;
}

export interface CreateProjectExecuteCallbacks {
  onStage?: (stage: CreateProjectStage) => void;
  /** Fraction 0..1 of the raw file upload to storage. */
  onUploadProgress?: (fraction: number) => void;
  /** Async import path only: the server-side job id was created; the task now survives reloads. */
  onJobEnqueued?: (jobId: string) => void;
  /** The import reported how many Final Draft tags it kept and skipped. */
  onFinalDraftTags?: (counts: FinalDraftTagCounts) => void;
}

const reportFinalDraftTags = (response: unknown, callbacks: CreateProjectExecuteCallbacks) => {
  const counts = finalDraftTagCountsFromImport(response);
  if (counts) callbacks.onFinalDraftTags?.(counts);
};

/** useAxios unwraps Axios to the Nest envelope `{ data: T }` at runtime; types still look like AxiosResponse. */
const unwrapCreatedProject = (response: unknown): ProjectDataType => {
  const envelope = response as { data?: ProjectDataType } | ProjectDataType | null | undefined;
  if (envelope && typeof envelope === "object" && "data" in envelope && envelope.data?.project_id) {
    return envelope.data;
  }
  return envelope as ProjectDataType;
};

const convertStateCodeToFullName = (stateCode: string): string | null => {
  if (!stateCode) return null;

  const state = US_STATES.find((s) => s.value === stateCode);
  return state ? state.label : stateCode; // Return original if not found
};

// On-prem only (no cloud upload): the backend handles versioning and storage paths.
const generateFilePath = (fileName: string, profileUserId?: string): string => {
  return `documents/${profileUserId}/${fileName}`;
};

const placeholderScene = (): SceneType => ({
  type: "scene",
  scene_heading: "Untitled Scene",
  int_ext: "",
  scene_number: null,
  sequence: null,
  time: null,
  location: null,
  page: null,
  length: null,
  paragraphs: [{ type: "action", blocks: [{ v: "" }] }],
});

const normalizeParsedContent = (parsedData: ParsedContent | null): ParsedContent | null => {
  if (!parsedData) return parsedData;
  try {
    const scenes = Array.isArray(parsedData.scenes) ? parsedData.scenes : [];
    const cleanScenes: SceneType[] = scenes
      .filter((s): s is NonNullable<ParsedSceneLoose> => Boolean(s) && typeof s === "object")
      .map((s) => {
        // Backend rule: send non-empty string OR null/omit; never send ""
        const primary = typeof s.location === "string" ? s.location.trim() : "";
        const legacy = typeof s._location === "string" ? s._location.trim() : "";
        const locationField: string | null =
          primary.length > 0 ? primary : legacy.length > 0 ? legacy : null;
        const result: SceneType = {
          type: String((s as Partial<SceneType>).type || ""),
          scene_heading: String((s as Partial<SceneType>).scene_heading || ""),
          int_ext: String((s as Partial<SceneType>).int_ext || ""),
          scene_number: (s as Partial<SceneType>).scene_number ?? null,
          sequence: (s as Partial<SceneType>).sequence ?? null,
          time: ((s as Partial<SceneType>).time as string | null) ?? null,
          location: locationField,
          page: ((s as Partial<SceneType>).page as string | null) ?? null,
          length: ((s as Partial<SceneType>).length as string | null) ?? null,
          paragraphs: s.paragraphs || [],
        };
        return result;
      });
    // Backend requires at least one scene (ArrayMinSize(1)); add placeholder when parse returns empty
    const scenesToSend = cleanScenes.length > 0 ? cleanScenes : [placeholderScene()];
    return { ...parsedData, scenes: scenesToSend };
  } catch {
    const fallbackScenes = Array.isArray(parsedData?.scenes) ? parsedData.scenes : [];
    if (fallbackScenes.length === 0) {
      return { ...parsedData, scenes: [placeholderScene()] };
    }
    return parsedData;
  }
};

const uploadOriginalFile = async (
  axios: AxiosInstance,
  queryClient: QueryClient,
  file: File,
  onUploadProgress?: (fraction: number) => void
): Promise<string> => {
  if (process.env.REACT_APP_ON_PREM === "true") {
    const profile = getProfileFromCache(queryClient);
    const profileUserId = profile?.user?.user_id || profile?.user_id;
    return generateFilePath(file.name, profileUserId);
  }

  // Same signed temporary upload the async import path uses. A failed upload stops
  // here rather than creating a project or version with no stored original.
  return uploadScreenplayViaSignedUrl(axios, USER_API.temporaryUploadUrl, file, onUploadProgress);
};

/**
 * Runs the full create-project chain (storage upload -> POST /projects -> optional
 * version-settings patch). Pure async function so it can run detached from any
 * component, e.g. inside BackgroundTasksProvider while the user keeps working.
 */
export const executeCreateProject = async (
  deps: CreateProjectExecuteDeps,
  input: CreateProjectExecuteInput,
  callbacks: CreateProjectExecuteCallbacks = {}
): Promise<ProjectDataType> => {
  const { axios, queryClient } = deps;
  const { formData, file, parsedData, mode, existingProject, source, workspaceId } = input;
  const { onStage, onUploadProgress } = callbacks;
  const resolvedSource: ProjectCreateSource = source ?? "import";

  // New-project imports go through the async server-side pipeline when enabled;
  // versions and blank projects keep the direct path.
  if (isAsyncImportEnabled() && mode !== "version" && resolvedSource === "import" && file) {
    let jobEnqueued = false;
    try {
      return await executeCreateProjectViaImportJob(deps, input, callbacks, (jobId) => {
        jobEnqueued = true;
        callbacks.onJobEnqueued?.(jobId);
      });
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const endpointMissing = status === 404 || status === 405 || status === 501;
      // Deploy skew: frontend live before the backend import-jobs feature.
      // Only safe to retry the legacy path while no server-side job exists.
      if (!jobEnqueued && endpointMissing) {
        // fall through to the legacy synchronous path below
      } else {
        throw err;
      }
    }
  }

  // Convert state code to full state name for API (legacy); production base syncs via version settings.
  const fullStateName = formData.features.production_state
    ? convertStateCodeToFullName(formData.features.production_state)
    : null;
  const productionLocation = formData.features.production_location;

  // Handle file upload with proper versioning (import / version only)
  let filePath = "";
  if (resolvedSource === "import" && file) {
    onStage?.("uploading");
    filePath = await uploadOriginalFile(axios, queryClient, file, onUploadProgress);
  }

  if (resolvedSource === "blank") {
    onStage?.("creating");
    const blankContent: ParsedContent = {
      title: formData.name,
      author: "",
      page_count: 1,
      scenes: [{ ...placeholderScene(), scene_number: 1, sequence: 1 }],
    };

    const projectData: Record<string, unknown> = {
      name: formData.name,
      production_type: formData.production_type,
      source: "blank",
      documents: [
        {
          file_path: "blank",
          file_size: 1,
          is_parsed_successfully: false,
          content: blankContent,
        },
      ],
      features: {
        has_budget: formData.features.has_budget,
        has_coverage: formData.features.has_coverage,
        has_character_breakdown: formData.features.has_character_breakdown,
        has_script_breakdown: formData.features.has_script_breakdown,
        has_schedule: formData.features.has_schedule,
        max_budget: formData.features.has_budget ? formData.features.max_budget : undefined,
        schedule_start_date: formData.features.has_schedule
          ? formData.features.schedule_start_date
          : undefined,
        production_state: fullStateName as ProductionState | null,
      },
    };

    if (formData.description && formData.description.trim() !== "") {
      projectData.description = formData.description;
    }
    if (projectData.features && typeof projectData.features === "object") {
      const feats = projectData.features as Record<string, unknown>;
      if (feats.max_budget === undefined || !feats.has_budget) {
        delete feats.max_budget;
      }
    }

    if (mode !== "version" && workspaceId) {
      projectData.workspace_id = workspaceId;
    }

    const response = await axios.post(USER_API.projects, projectData, {
      params: { mode },
      timeout: PROJECT_CREATE_TIMEOUT_MS,
    });
    return unwrapCreatedProject(response);
  }

  const normalizedContent = normalizeParsedContent(parsedData);

  // Create the typed project data (using internal types)
  const typedProjectData: CreateProjectDataType = {
    name: formData.name,
    description: formData.description,
    production_type: formData.production_type as ProjectTypeKey,
    documents: [
      {
        file_path: filePath,
        file_size: file?.size || 0,
        is_parsed_successfully: !!normalizedContent,
        parsed_data:
          normalizedContent as unknown as CreateProjectDataType["documents"][number]["parsed_data"],
      },
    ],
    features: {
      has_budget: formData.features.has_budget,
      has_coverage: formData.features.has_coverage,
      has_character_breakdown: formData.features.has_character_breakdown,
      has_script_breakdown: formData.features.has_script_breakdown,
      has_schedule: formData.features.has_schedule,
      max_budget: formData.features.has_budget ? formData.features.max_budget : undefined,
      schedule_start_date: formData.features.has_schedule
        ? formData.features.schedule_start_date
        : undefined,
      production_state: fullStateName as ProductionState | null,
    },
  };

  // Convert to API format (parsed_data → content)
  const projectData = {
    name: typedProjectData.name,
    description: typedProjectData.description,
    production_type: typedProjectData.production_type,
    documents: [
      {
        file_path: typedProjectData.documents[0].file_path,
        file_size: typedProjectData.documents[0].file_size,
        is_parsed_successfully: typedProjectData.documents[0].is_parsed_successfully,
        content: typedProjectData.documents[0].parsed_data,
      },
    ],
    features: {
      has_budget: typedProjectData.features.has_budget,
      has_coverage: typedProjectData.features.has_coverage,
      has_character_breakdown: typedProjectData.features.has_character_breakdown,
      has_script_breakdown: typedProjectData.features.has_script_breakdown,
      has_schedule: typedProjectData.features.has_schedule,
      max_budget: typedProjectData.features.max_budget,
      schedule_start_date: typedProjectData.features.schedule_start_date,
      production_state: typedProjectData.features.production_state,
    },
  };

  // Clean up undefined/empty values (following existing pattern)
  if (projectData.description === "") {
    delete (projectData as { description?: string }).description;
  }
  if (projectData.features.max_budget === undefined || !projectData.features.has_budget) {
    delete (projectData.features as { max_budget?: number }).max_budget;
  }

  // Add project_id for version creation
  if (mode === "version" && existingProject) {
    (projectData as { project_id?: unknown }).project_id = (
      existingProject as { project_id: unknown }
    ).project_id;
  }

  if (mode !== "version" && workspaceId) {
    (projectData as { workspace_id?: string }).workspace_id = workspaceId;
  }

  if (mode !== "version") {
    (projectData as { source?: string }).source = "import";
  }

  const correlationId = crypto.randomUUID?.() || Math.random().toString(36).slice(2);
  const idempotencyKey = crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`;

  // Idempotency on both modes: a retried/timed-out create must not duplicate the project.
  const headers: Record<string, string> = {
    "Idempotency-Key": idempotencyKey,
  };
  if (mode === "version") {
    headers["X-Correlation-Id"] = correlationId;
  }

  onStage?.("creating");
  const response = await axios.post(USER_API.projects, projectData, {
    headers,
    params: { mode },
    timeout: PROJECT_CREATE_TIMEOUT_MS,
  });

  reportFinalDraftTags(response, callbacks);
  const createdProject = unwrapCreatedProject(response);
  const documentId =
    createdProject?.active_document_id ??
    (createdProject as { documents?: Array<{ document_id?: string }> })?.documents?.[0]
      ?.document_id;

  if (createdProject?.project_id && documentId && productionLocation?.name?.trim()) {
    onStage?.("finalizing");
    await axios.patch(
      USER_API.patchVersionSettings(String(createdProject.project_id), String(documentId)),
      {
        production_location: {
          name: productionLocation.name.trim(),
          ...(productionLocation.place_id ? { place_id: productionLocation.place_id } : {}),
        },
      }
    );
  }

  return createdProject;
};

// ---------------------------------------------------------------------------
// Async import-job path (Phase 2): direct-to-GCS upload, then the backend
// parses and creates the project in a background worker. The job survives
// page reloads; completion is polled from GET /projects/import-jobs/:id.
// ---------------------------------------------------------------------------

export interface ImportJobRow {
  job_id: string;
  status: "queued" | "parsing" | "creating" | "done" | "failed";
  file_name?: string | null;
  workspace_id?: string | null;
  project_id?: string | number | null;
  error?: string | null;
  settings?: { name?: string } | null;
  expected_timeout_at?: string | null;
}

const IMPORT_JOB_POLL_MS = 4000;
const IMPORT_JOB_MAX_WAIT_MS = 15 * 60 * 1000;

const unwrapEnvelope = <T>(value: unknown): T => {
  const envelope = value as { data?: T } | T | null | undefined;
  if (envelope && typeof envelope === "object" && "data" in (envelope as object)) {
    const inner = (envelope as { data?: T }).data;
    if (inner !== undefined && inner !== null) return inner;
  }
  return envelope as T;
};

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const importJobStageFor = (status: ImportJobRow["status"]): CreateProjectStage => {
  switch (status) {
    case "parsing":
      return "parsing";
    case "creating":
      return "creating";
    default:
      return "creating";
  }
};

const fetchCreatedProject = async (
  axios: AxiosInstance,
  projectId: string
): Promise<ProjectDataType> => {
  const response = await axios.get(`${USER_API.projects}/${projectId}?include_versions=true`);
  return unwrapCreatedProject(response);
};

/** Polls an import job until it reaches a terminal state, then resolves the created project. */
export const awaitImportJob = async (
  deps: CreateProjectExecuteDeps,
  jobId: string,
  callbacks: CreateProjectExecuteCallbacks = {}
): Promise<ProjectDataType> => {
  const { axios } = deps;
  const startedAt = Date.now();

  for (;;) {
    const response = await axios.get(USER_API.importJob(jobId));
    const job = unwrapEnvelope<ImportJobRow>(response as unknown);

    if (job.status === "done" && job.project_id) {
      reportFinalDraftTags(response, callbacks);
      callbacks.onStage?.("finalizing");
      return fetchCreatedProject(axios, String(job.project_id));
    }
    if (job.status === "failed") {
      throw new Error(job.error || "Project import failed. Please try again.");
    }
    callbacks.onStage?.(importJobStageFor(job.status));

    if (Date.now() - startedAt > IMPORT_JOB_MAX_WAIT_MS) {
      throw new Error("Project import timed out. Please try again.");
    }
    await sleep(IMPORT_JOB_POLL_MS);
  }
};

const executeCreateProjectViaImportJob = async (
  deps: CreateProjectExecuteDeps,
  input: CreateProjectExecuteInput,
  callbacks: CreateProjectExecuteCallbacks,
  onJobEnqueued?: (jobId: string) => void
): Promise<ProjectDataType> => {
  const { axios } = deps;
  const { formData, file, mode, workspaceId } = input;
  const { onStage, onUploadProgress } = callbacks;
  if (!file) {
    throw new Error("A screenplay file is required for import.");
  }

  onStage?.("uploading");
  const uploadUrlResponse = await axios.get(USER_API.temporaryUploadUrl);
  const { url, file_path } = unwrapEnvelope<{ url: string; file_path: string }>(
    uploadUrlResponse as unknown
  );

  // Signed GCS PUT: raw axios on purpose; the app instance would attach auth
  // headers the signature does not cover.
  const { default: rawAxios } = await import("axios");
  await rawAxios.put(url, file, {
    headers: { "Content-Type": file.type || "application/octet-stream" },
    onUploadProgress: (event) => {
      if (event.total) {
        onUploadProgress?.(event.loaded / event.total);
      }
    },
  });

  const fullStateName = formData.features.production_state
    ? convertStateCodeToFullName(formData.features.production_state)
    : null;

  const settings: Record<string, unknown> = {
    name: formData.name,
    production_type: formData.production_type,
    source: "import",
    features: {
      has_budget: formData.features.has_budget,
      has_coverage: formData.features.has_coverage,
      has_character_breakdown: formData.features.has_character_breakdown,
      has_script_breakdown: formData.features.has_script_breakdown,
      has_schedule: formData.features.has_schedule,
      ...(formData.features.has_budget && formData.features.max_budget !== undefined
        ? { max_budget: formData.features.max_budget }
        : {}),
      ...(formData.features.has_schedule && formData.features.schedule_start_date
        ? { schedule_start_date: formData.features.schedule_start_date }
        : {}),
      production_state: fullStateName as ProductionState | null,
    },
  };
  if (formData.description && formData.description.trim() !== "") {
    settings.description = formData.description;
  }
  if (mode !== "version" && workspaceId) {
    settings.workspace_id = workspaceId;
  }

  onStage?.("creating");
  const idempotencyKey = crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`;
  const enqueueResponse = await axios.post(
    USER_API.importJobs,
    {
      file_path,
      file_name: file.name,
      file_size: file.size,
      settings,
    },
    { headers: { "Idempotency-Key": idempotencyKey } }
  );
  const { job_id } = unwrapEnvelope<{ job_id: string }>(enqueueResponse as unknown);
  onJobEnqueued?.(job_id);

  const project = await awaitImportJob(deps, job_id, callbacks);

  // Production location patch mirrors the sync path's finalizing step.
  const productionLocation = formData.features.production_location;
  const documentId =
    project?.active_document_id ??
    (project as { documents?: Array<{ document_id?: string }> })?.documents?.[0]?.document_id;
  if (project?.project_id && documentId && productionLocation?.name?.trim()) {
    onStage?.("finalizing");
    await axios.patch(
      USER_API.patchVersionSettings(String(project.project_id), String(documentId)),
      {
        production_location: {
          name: productionLocation.name.trim(),
          ...(productionLocation.place_id ? { place_id: productionLocation.place_id } : {}),
        },
      }
    );
  }

  return project;
};

/** Lists the current user's recent import jobs (for reload rehydration). */
export const listImportJobs = async (deps: CreateProjectExecuteDeps): Promise<ImportJobRow[]> => {
  const response = await deps.axios.get(USER_API.importJobs);
  const rows = unwrapEnvelope<ImportJobRow[]>(response as unknown);
  return Array.isArray(rows) ? rows : [];
};

export interface CreateProjectErrorInfo {
  canceled: boolean;
  httpStatus?: number;
  apiErrorCode?: string;
  axiosCode?: string;
  message?: string;
}

export const describeCreateProjectError = (err: unknown): CreateProjectErrorInfo => {
  const canceled = (() => {
    if (!err || typeof err !== "object") return false;
    const e = err as { code?: string; name?: string; message?: string };
    if (e.code === "ERR_CANCELED" || e.name === "CanceledError" || e.name === "AbortError") {
      return true;
    }
    if (typeof e.message === "string" && e.message.toLowerCase().includes("canceled")) {
      return true;
    }
    return isAxiosError(err) && err.code === "ERR_CANCELED";
  })();

  return {
    canceled,
    httpStatus: (err as { response?: { status?: number } })?.response?.status,
    apiErrorCode: (err as { response?: { data?: { code?: string } } })?.response?.data?.code,
    axiosCode: (err as { code?: string })?.code,
    message:
      (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
      (err as { message?: string })?.message,
  };
};
